<template>
  <v-card
    class="ma-1"
    width="100%"
  >
    <v-row class="ma-0">
      <v-col
        class="mt-3 pt-0 pb-0"
        cols="3"
      >
        <div class="d-flex align-items-center gap-2">
          <v-btn
            icon
            to="/utils"
            class="mr-2 ml-1"
            title="Back to Utilities"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <b-form-input
            v-model="searchText"
            :disabled="getCheckedStructures && getCheckedStructures.length > 0"
            title="Search PubChem by name, CAS RN, or InChI Key"
            placeholder="Search PubChem by name, CAS RN, or InChI Key"
            type="search"
          />
        </div>
      </v-col>
      <v-col
        class="pt-0 pb-0"
        cols="3"
      >
        <v-select
          v-model="selectedHeaders"
          v-b-tooltip
          :items="headersSelection"
          item-text="text"
          item-value="value"
          label="Columns:"
          multiple
          title="Columns"
        />
      </v-col>

      <v-col
        class="pt-0 pb-0"
        cols="2"
      >
        <v-select
          v-model="linkBy"
          :items="[
            { id: 'annotation', text: 'annotation' },
            { id: 'casrn', text: 'CAS RN' },
            { id: 'inchiKey', text: 'inchiKey' },
          ]"
          item-value="id"
          label="Link by:"
          title="Link databases by"
        />
      </v-col>
      <v-col
        class="pt-0 pb-0"
        cols="2"
      >
        <v-select
          v-model="reportName"
          :items="reports"
          clearable
          item-text="title"
          item-value="name"
          label="Integrity Reports"
        />
      </v-col>
      <v-col class="pt-0 pb-0">
        <v-btn-toggle
          class="mr-6 mt-3 d-flex justify-end"
          dense
        >
          <v-btn
            v-b-tooltip
            :disabled="!(getCheckedStructures.length !== 0 || totalItems < 10000)"
            title="Export to XLSX"
            @click="onExport('XLSX')"
            aria-label="Export to XLSX"
          >
            <v-icon>mdi-file-excel-outline</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-row class="m-0">
      <v-col
        class="pt-0 pb-0"
        cols="1"
      >
        <v-switch
          v-model="showDetails"
          class="mt-3"
          label="Details"
          title="Show/hide additional information"
        />
      </v-col>
      <v-col
        class="pt-0 pb-0"
        cols="1"
      >
        <v-switch
          v-model="highlightDiff"
          class="mt-3"
          label="Highlight"
          title="Highlight differences"
        />
      </v-col>
      <v-col
        class="pt-0 pb-0"
        cols="auto"
      >
        <v-switch
          v-model="ccImage"
          class="mt-3"
          inline
          label="Common Chemistry Image"
          title="Use original Common Chemistry Image"
        />
      </v-col>
      <v-col
        class="pt-0 pb-0"
        cols="auto"
      >
        <v-switch
          v-model="DTXSIDmatch"
          class="mt-3"
          inline
          label="DTXSID match only"
          title="Show Chemicals with DTXSID match only"
        />
      </v-col>
    </v-row>
    <v-data-table
      :footer-props="{ 'items-per-page-options': [10, 50] }"
      :headers="getheaders"
      :hide-default-footer="getCheckedStructures && getCheckedStructures.length > 0"
      :items="getitems"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      class="mt-3 elevation-5"
      disable-sort
    >
      <!--@dblclick:row="(item, slot) => slot.expand(!slot.isExpanded)"
      single-expand
      :expanded.sync="expanded"
      item-key="pubchemRecord.cid"-->
      <template #[`item.pubchem`]="{ item }">
        <v-row class="d-flex flex-column mb-2 mt-2">
          <v-col class="d-flex flex-column">
            <UChem
              v-if="item.pubchemRecord"
              :highlight-diff="false"
              :item="item.pubchemRecord"
              :search-text="searchText"
              :show-details="showDetails"
              linktype="pubchem"
            />
          </v-col>
        </v-row>
      </template>
      <template #[`item.dsstox`]="{ item }">
        <v-row class="d-flex flex-column mb-2 mt-2">
          <v-col
            v-for="i in item.dss_records"
            :key="i.chemId"
            class="d-flex flex-column"
          >
            <UChem
              :highlight-diff="highlightDiff"
              :item="i"
              :original="item.pubchemRecord"
              :search-text="searchText"
              :show-details="showDetails"
              linktype="dsstox"
            />
          </v-col>
        </v-row>
      </template>
      <template #[`item.comchem`]="{ item }">
        <v-row class="d-flex flex-column mb-2 mt-2">
          <v-col
            v-for="i in item.cc_records"
            :key="i.chemId"
            class="d-flex flex-column"
          >
            <UChem
              :highlight-diff="highlightDiff"
              :item="i"
              :original="item.pubchemRecord ? item.pubchemRecord : item.dss_records[0].chemical"
              :search-text="searchText"
              :show-details="showDetails"
              :use-own-image="ccImage"
              linktype="comchem"
            />
          </v-col>
        </v-row>
      </template>
      <!--template #expanded-item="{ headers, item }">
        <v-container fluid :colspan="headers.length">
          <v-row>
            <v-col>
              <pre>{{ item.pubchemRecord }}</pre>
            </v-col>
            <v-col v-for="i in item.dss_records" :key="i.chemId">
              <pre>{{ i.chemical }}</pre>
            </v-col>
          </v-row>
        </v-container>
      </template-->

      <template #no-data>No items found</template>
    </v-data-table>

    <v-progress-linear
      v-if="isLoadingOrDownloading"
      indeterminate
    />
  </v-card>
</template>

<script>
import { download, getDatedFilename, RESOLVER_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import UChem from '@/components/utils/UChemCardWithDetails.vue'
import { mapGetters } from 'vuex'

export default {
  components: { UChem },

  data: () => ({
    showDetails: true,
    highlightDiff: true,
    ccImage: true,
    expanded: [],
    options: {},
    itemsPerPage: 10,
    headersSelection: [
      { text: 'PubChem', value: 'pubchem' },
      { text: 'DSSTox', value: 'dsstox' },
      { text: 'Common Chemistry', value: 'comchem' },
    ],
    headers: [
      {
        text: 'PubChem',
        value: 'pubchem',
        align: 'left',
        width: '33%',
        sortable: false,
        divider: true,
      },
      {
        text: 'DSSTox',
        value: 'dsstox',
        align: 'left',
        width: '33%',
        sortable: false,
        divider: true,
      },
      {
        text: 'Common Chemistry',
        value: 'comchem',
        align: 'left',
        width: '33%',
        sortable: false,
        divider: true,
      },
    ],
    selectedHeaders: ['pubchem', 'dsstox', 'comchem'],
    searchText: '',
    serverItems: [],
    reports: [],
    loading: true,
    downloading: false,
    totalItems: 0,
    searchBy: 'name',
    linkBy: 'annotation',
    reportName: null,
    DTXSIDmatch: false,
  }),

  computed: {
    ...mapGetters(['getCheckedStructures']),

    isLoadingOrDownloading() {
      return this.loading || this.downloading
    },

    placeholder() {
      return 'search pubchem by ' + this.searchBy
    },
    getheaders() {
      return this.headers.filter((h) => this.selectedHeaders.findIndex((o) => h.value === o) !== -1)
    },
    getitems() {
      return this.DTXSIDmatch ? this.serverItems.filter((h) => h.dss_records.length > 0) : this.serverItems
    },
  },

  watch: {
    reportName: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    linkBy: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    searchBy: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    searchText: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    options: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
  },

  methods: {
    onExport(format) {
      this.downloading = true
      this.$axios
        .post(
          `${RESOLVER_URL}/exportpubchemlist?search=${this.searchText}&linkBy=${this.linkBy}`,
          {
            chemicals: this.getCheckedStructures,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          let filename = getDatedFilename('pubchemintegrity', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.downloading = false
        })
    },
    getItems({ page, itemsPerPage }) {
      this.loading = true
      this.$axios
        .post(
          `${RESOLVER_URL}/getpubchemlist?page=${page}&itemsPerPage=${itemsPerPage}&search=${this.searchText}&linkBy=${
            this.linkBy
          }&report=${this.reportName ? this.reportName : ''}`,
          {
            chemicals: this.getCheckedStructures,
          },
        )
        .then((response) => {
          this.serverItems = response.data.items
          this.totalItems = response.data.total
        })
        .catch((error) => {
          showErrorToast(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    loadItems() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      this.getItems({ page: page, itemsPerPage: itemsPerPage })
    },
  },

  mounted() {
    this.loading = true
    this.$axios
      .get(`${RESOLVER_URL}/getintegrityreports`)
      .then((response) => {
        this.reports = response.data
      })
      .catch((error) => {
        showErrorToast(error)
      })
      .finally(() => {
        this.loading = false
      })
  },
}
</script>
